import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Onboarding state.
 *
 * Replaces the previous React-context + localStorage approach. The context
 * model made the burn bug possible: writing the "visited" flag and starting the
 * tour were two unrelated statements in a useEffect with no single owner of the
 * transition. Here the transition is the reducer, so "a tour that failed stays
 * incomplete" is a three-line test.
 *
 * Progress is persisted per user by persistenceListenerMiddleware — never
 * globally, so a second author on a shared machine still gets onboarded.
 */

export type TourId =
  | "editor360StageA"
  | "editor360StageB"
  | "editor3DStageA"
  | "editor3DStageB"
  | "dashboardStageA"
  | "dashboardStageB";

/**
 * Every id that survives a reload. setOnboardingHydrated filters stored
 * progress against this array, so an id missing here is silently dropped on
 * load and its tour reruns forever.
 */
export const TOUR_IDS: TourId[] = [
  "editor360StageA",
  "editor360StageB",
  "editor3DStageA",
  "editor3DStageB",
  "dashboardStageA",
  "dashboardStageB",
];

export type OnboardingWelcomeChoice = "unseen" | "started" | "dismissed";

/** The subset that survives a reload. */
export interface OnboardingProgress {
  completedTours: TourId[];
  abandonedTours: Partial<Record<TourId, number>>;
  dismissedHints: string[];
  welcomeChoice: OnboardingWelcomeChoice;
}

export interface OnboardingState extends OnboardingProgress {
  /** Null when no tour is running. */
  activeTourId: TourId | null;
  activeStepIndex: number;
  isShortcutsOverlayOpen: boolean;
  /** False until stored progress has been read, so nothing auto-runs too early. */
  isHydrated: boolean;
  /**
   * Tours that failed to resolve their anchors this session. NOT persisted —
   * a fixed anchor should work on the next load without the user clearing
   * anything. But it has to be remembered for the current session, or the host
   * restarts the tour the instant the runner aborts it, forever.
   */
  abortedThisSession: TourId[];
}

const initialState: OnboardingState = {
  activeTourId: null,
  activeStepIndex: 0,
  completedTours: [],
  abandonedTours: {},
  dismissedHints: [],
  welcomeChoice: "unseen",
  isShortcutsOverlayOpen: false,
  isHydrated: false,
  abortedThisSession: [],
};

const onboardingSlice = createSlice({
  name: "onboarding",
  initialState,
  reducers: {
    /** Applies stored progress. Never starts a tour by itself. */
    setOnboardingHydrated: (
      state,
      action: PayloadAction<Partial<OnboardingProgress> | null>
    ) => {
      // Hydration is a full user boundary, not a merge with whoever last used
      // this Redux store.
      state.completedTours = [];
      state.abandonedTours = {};
      state.dismissedHints = [];
      state.welcomeChoice = "unseen";
      state.activeTourId = null;
      state.activeStepIndex = 0;
      state.abortedThisSession = [];

      const stored = action.payload;
      if (stored) {
        if (Array.isArray(stored.completedTours)) {
          state.completedTours = stored.completedTours.filter((id) =>
            TOUR_IDS.includes(id)
          );
        }
        if (stored.abandonedTours && typeof stored.abandonedTours === "object") {
          state.abandonedTours = stored.abandonedTours;
        }
        if (Array.isArray(stored.dismissedHints)) {
          state.dismissedHints = stored.dismissedHints;
        }
        if (
          stored.welcomeChoice === "unseen" ||
          stored.welcomeChoice === "started" ||
          stored.welcomeChoice === "dismissed"
        ) {
          state.welcomeChoice = stored.welcomeChoice;
        } else {
          // Records written before the welcome screen existed must not greet
          // established users again. Any saved progress proves they already
          // entered the onboarding flow.
          const hasLegacyProgress =
            (Array.isArray(stored.completedTours) &&
              stored.completedTours.length > 0) ||
            (stored.abandonedTours &&
              typeof stored.abandonedTours === "object" &&
              Object.keys(stored.abandonedTours).length > 0) ||
            (Array.isArray(stored.dismissedHints) &&
              stored.dismissedHints.length > 0);

          if (hasLegacyProgress) {
            state.welcomeChoice = "started";
          }
        }
      }
      state.isHydrated = true;
    },

    /** Clears all onboarding state without triggering persistence. */
    setOnboardingCleared: () => initialState,

    setWelcomeChoice: (
      state,
      action: PayloadAction<Exclude<OnboardingWelcomeChoice, "unseen">>
    ) => {
      state.welcomeChoice = action.payload;
    },

    /** Begins a tour, optionally resuming at a stored step. */
    setTourStarted: (
      state,
      action: PayloadAction<{ tourId: TourId; stepIndex?: number }>
    ) => {
      state.activeTourId = action.payload.tourId;
      state.activeStepIndex = action.payload.stepIndex ?? 0;
    },

    setTourStepIndex: (state, action: PayloadAction<number>) => {
      state.activeStepIndex = Math.max(0, action.payload);
    },

    /**
     * The ONLY action that marks a tour as done. Dispatched on joyride's
     * FINISHED status and nothing else.
     */
    setTourCompleted: (state, action: PayloadAction<TourId>) => {
      if (!state.completedTours.includes(action.payload)) {
        state.completedTours.push(action.payload);
      }
      delete state.abandonedTours[action.payload];
      state.activeTourId = null;
      state.activeStepIndex = 0;
    },

    /** User closed or skipped. Records where they stopped so it can resume. */
    setTourAbandoned: (
      state,
      action: PayloadAction<{ tourId: TourId; stepIndex: number }>
    ) => {
      state.abandonedTours[action.payload.tourId] = Math.max(
        0,
        action.payload.stepIndex
      );
      state.activeTourId = null;
      state.activeStepIndex = 0;
    },

    /**
     * A target failed to resolve. Persists nothing at all, so the tour is
     * retried on the next load once the anchor is fixed — the inverse of the
     * old SetVisited-before-success behaviour.
     */
    setTourAborted: (state, action: PayloadAction<TourId>) => {
      if (!state.abortedThisSession.includes(action.payload)) {
        state.abortedThisSession.push(action.payload);
      }
      state.activeTourId = null;
      state.activeStepIndex = 0;
    },

    setHintDismissed: (state, action: PayloadAction<string>) => {
      if (!state.dismissedHints.includes(action.payload)) {
        state.dismissedHints.push(action.payload);
      }
    },

    setShortcutsOverlayOpen: (state, action: PayloadAction<boolean>) => {
      state.isShortcutsOverlayOpen = action.payload;
    },

    /** "Reset onboarding" from the help menu. */
    setOnboardingReset: (state) => {
      state.completedTours = [];
      state.abandonedTours = {};
      state.dismissedHints = [];
      state.welcomeChoice = "unseen";
      state.activeTourId = null;
      state.activeStepIndex = 0;
      // An explicit reset is the user asking to try again from scratch.
      state.abortedThisSession = [];
    },
  },
});

export const {
  setOnboardingHydrated,
  setOnboardingCleared,
  setWelcomeChoice,
  setTourStarted,
  setTourStepIndex,
  setTourCompleted,
  setTourAbandoned,
  setTourAborted,
  setHintDismissed,
  setShortcutsOverlayOpen,
  setOnboardingReset,
} = onboardingSlice.actions;

/* ---------------------------------------------------------------- selectors
 * Every selector returns either a primitive or a stored reference. None build
 * a fresh array or object per call — useSelector treats a new identity as a
 * change, which produces render loops that surface as Jest worker crashes.
 */

interface OnboardingRoot {
  onboarding: OnboardingState;
}

export const selectActiveTourId = (state: OnboardingRoot) =>
  state.onboarding.activeTourId;

export const selectActiveStepIndex = (state: OnboardingRoot) =>
  state.onboarding.activeStepIndex;

export const selectCompletedTours = (state: OnboardingRoot) =>
  state.onboarding.completedTours;

export const selectDismissedHints = (state: OnboardingRoot) =>
  state.onboarding.dismissedHints;


export const selectIsShortcutsOverlayOpen = (state: OnboardingRoot) =>
  state.onboarding.isShortcutsOverlayOpen;

export const selectAbortedThisSession = (state: OnboardingRoot) =>
  state.onboarding.abortedThisSession;

export const selectIsOnboardingHydrated = (state: OnboardingRoot) =>
  state.onboarding.isHydrated;

export const selectWelcomeChoice = (state: OnboardingRoot) =>
  state.onboarding.welcomeChoice;

export const selectIsTourCompleted =
  (tourId: TourId) => (state: OnboardingRoot) =>
    state.onboarding.completedTours.includes(tourId);

export const selectAbandonedTours = (state: OnboardingRoot) =>
  state.onboarding.abandonedTours;

export const selectAbandonedTourStep =
  (tourId: TourId) => (state: OnboardingRoot) =>
    state.onboarding.abandonedTours[tourId] ?? null;

export const selectIsHintDismissed =
  (hintId: string) => (state: OnboardingRoot) =>
    state.onboarding.dismissedHints.includes(hintId);

/** Shape handed to the persistence listener. */
export const selectOnboardingProgress = (
  state: OnboardingRoot
): OnboardingProgress => ({
  completedTours: state.onboarding.completedTours,
  abandonedTours: state.onboarding.abandonedTours,
  dismissedHints: state.onboarding.dismissedHints,
  welcomeChoice: state.onboarding.welcomeChoice,
});

export default onboardingSlice.reducer;
